/**
 * Lightweight product-analytics client (web).
 * Buffers events and flushes to /api/analytics/track. Never throws into the app.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEVICE_KEY = "trata_analytics_device_id";
const SESSION_IDLE_MS = 30 * 60 * 1000;
const FLUSH_INTERVAL_MS = 8000;

// Prefix-matched: navigating to one of these logs a service_click so the Admin
// Portal shows which services get used — no per-button instrumentation needed.
const SERVICE_ROUTES = [
  ["/ambulance", "Emergency / Ambulance"],
  ["/emergency", "Emergency / Ambulance"],
  ["/paramedic", "Paramedic"],
  ["/doctors", "Find Doctors"],
  ["/find-care", "Find Hospitals"],
  ["/find-labs", "Lab Tests"],
  ["/lab-booking", "Lab Tests"],
  ["/diagnostic-centres", "Diagnostic Centres"],
  ["/find-pharmacies", "Pharmacy"],
  ["/buy-medicine", "Pharmacy"],
  ["/order-medicines", "Pharmacy"],
  ["/mental-wellness", "Mental Wellness"],
  ["/abha", "ABHA / ABDM"],
  ["/assistant", "Dr. Sanjeevani"],
  ["/ai-doctor", "AI Doctor"],
  ["/records", "Health Records"],
  ["/rewards", "Rewards"],
  ["/cancer-care", "Cancer Care"],
  ["/senior-care", "Senior Care"],
  ["/at-home-care", "At-Home Care"],
];

const rand = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

let sessionId = "";
let lastActivity = 0;
let userId = null;
let phone = null;
let queue = [];
let started = false;
let currentScreen = null;
let screenEnteredAt = 0;
let lastFunnelAt = 0;

function deviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) { id = `w-${rand()}`; localStorage.setItem(DEVICE_KEY, id); }
    return id;
  } catch { return `w-${rand()}`; }
}

function rollSession() {
  const now = Date.now();
  if (!sessionId || now - lastActivity > SESSION_IDLE_MS) sessionId = `s-${rand()}`;
  lastActivity = now;
}

function enqueue(e) {
  rollSession();
  queue.push({ device_id: deviceId(), session_id: sessionId, user_id: userId, phone, platform: "web", ...e });
  if (queue.length >= 25) flush();
}

export function flush() {
  if (!queue.length || !BACKEND_URL) return;
  const batch = queue.splice(0, queue.length);
  const body = JSON.stringify({ events: batch });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${BACKEND_URL}/api/analytics/track`, new Blob([body], { type: "application/json" }));
    } else {
      fetch(`${BACKEND_URL}/api/analytics/track`, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    }
  } catch {
    if (queue.length < 100) queue = batch.concat(queue);
  }
}

export function initAnalytics() {
  if (started) return;
  started = true;
  rollSession();
  enqueue({ event_type: "app_open" });
  enqueue({ event_type: "session_start" });
  setInterval(flush, FLUSH_INTERVAL_MS);
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flush(); });
    window.addEventListener("beforeunload", flush);
  }
}

export function setAnalyticsUser(u = {}) {
  if (u.userId !== undefined) userId = u.userId;
  if (u.phone !== undefined) phone = u.phone;
}

export function trackFunnel(stage, durationMs) {
  const now = Date.now();
  const d = typeof durationMs === "number" ? durationMs : (lastFunnelAt ? now - lastFunnelAt : undefined);
  lastFunnelAt = now;
  enqueue({ event_type: "funnel", event_name: stage, duration_ms: typeof d === "number" ? Math.round(d) : undefined });
}

export function trackServiceClick(service, screen) {
  enqueue({ event_type: "service_click", service, screen: screen || currentScreen || undefined });
}

export function trackError(message, screen) {
  enqueue({ event_type: "error", screen: screen || currentScreen || undefined, properties: { message: String(message).slice(0, 300) } });
}

export function trackApiCall(endpoint, ms, status) {
  enqueue({ event_type: "api_call", event_name: endpoint, duration_ms: Math.round(ms), status_code: status });
}

export function trackNavigation(path) {
  const now = Date.now();
  if (currentScreen && currentScreen !== path) {
    enqueue({ event_type: "screen_exit", screen: currentScreen, duration_ms: Math.max(0, now - screenEnteredAt) });
  }
  if (currentScreen === path) return;
  currentScreen = path;
  screenEnteredAt = now;
  enqueue({ event_type: "screen_view", screen: path });
  const match = SERVICE_ROUTES.find(([p]) => path === p || path.startsWith(p + "/"));
  if (match) enqueue({ event_type: "service_click", service: match[1], screen: path });
}
